/* eslint-disable no-invalid-this */
import React from 'react';
import {connect} from 'react-redux';
import {IRouteScreenProps, ISupplierUserProfile} from 'constants/interfaces';
import {
  Box,
  Button,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';
import {
  ArrowUpward,
  ArrowDownward,
  Search as SearchIcon,
} from '@material-ui/icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPlus, faPenToSquare} from '@fortawesome/pro-regular-svg-icons';

import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {_NumberWithSpaces, ESidePanelView, formatDate} from 'constants/index';
import {IFarmInputProgramInput, IUser} from 'constants/interfaces';
import {
  apiGetInputPrograms,
  apiGetSupportCustomers,
  apiGetUser,
} from 'constants/Api';
import {lshFilterInputProgramsForLinkedCustomers} from 'services/SupplierCustomerScope';

import './Programs.scss';

/* ────────────────────────────────────────────────
   Augmented row type
   ────────────────────────────────────────────── */
interface ProgramRow extends IFarmInputProgramInput {
  farmName: string;
  userId: number | string | null;
  createdDate: string;
  produce?: {
    id?: string;
    name?: string;
    [key: string]: any;
  };
}

type ProgramsProps = IRouteScreenProps & {
  user: ISupplierUserProfile;
  editState?: 'idle' | 'editing' | 'saved';
};

type ProgramsStateLocal = {
  search: string;
  sortAsc: boolean;
  page: number;
  rowsPerPage: number;
  sortField: string;
  programs: ProgramRow[];
  loading: boolean;
  user?: IUser;
  filterUserId?: string | number;
};

class Programs extends React.Component<ProgramsProps, ProgramsStateLocal> {
  state: ProgramsStateLocal = {
    search: '',
    sortAsc: false,
    page: 0,
    rowsPerPage: 10,
    sortField: 'createdDate',
    programs: [],
    loading: true,
    user: undefined,
    filterUserId: undefined,
  };

  /* ---------- helpers ---------- */

  openCreateProgram = () => {
    reduxToggleSidePanel(
      true,
      {panelType: ESidePanelView.ADD_PROGRAM, data: null},
      false,
    );
  };

  handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({search: e.target.value, page: 0});

  handleOpenProgram = (row: ProgramRow) => {
    if (!row) return;
    console.log('[Programs] navigating with userId →', row.userId);
    this.props.history.push({
      pathname: `/programs/${row.id}`,
      state: {userId: row.userId},
    });
  };

  /* ---------- lifecycle ---------- */

  componentDidMount() {
    this.loadUserAndPrograms();
  }

  componentDidUpdate(prevProps: ProgramsProps) {
    // Reload if userId query param changes
    const prevUserId = new URLSearchParams(prevProps.location.search).get(
      'userId',
    );
    const currentUserId = new URLSearchParams(this.props.location.search).get(
      'userId',
    );

    if (prevUserId !== currentUserId) {
      this.loadUserAndPrograms();
    }

    // Refresh programs when a program is updated via side panel
    if (
      prevProps.editState !== this.props.editState &&
      this.props.editState === 'saved'
    ) {
      this.loadPrograms();
      // Reset edit state
      reduxSetSidePanelEditState('idle');
    }
  }

  loadUserAndPrograms() {
    const queryParams = new URLSearchParams(this.props.location.search);
    const userId = queryParams.get('userId');

    if (!userId) {
      // No userId filter, load all programs
      this.setState({user: undefined, filterUserId: undefined}, () =>
        this.loadPrograms(),
      );
      return;
    }

    apiGetUser(userId)
      .then((user: IUser) => {
        this.setState({user, filterUserId: user?.id}, () =>
          this.loadPrograms(),
        );
      })
      .catch((err) => {
        console.error('Failed to fetch user:', err);
        this.setState({user: undefined, filterUserId: undefined}, () =>
          this.loadPrograms(),
        );
      });
  }

  loadPrograms = () => {
    this.setState({loading: true});
    Promise.all([apiGetInputPrograms(), apiGetSupportCustomers()])
      .then(([res, customers]) => {
        const scopedPrograms = lshFilterInputProgramsForLinkedCustomers(
          res,
          customers,
        );
        const programs: ProgramRow[] = scopedPrograms.map((p: any) => ({
          id: p.id,
          createdDate: p.createdDate || '',
          farmerId: p.farmerId,
          produceId: p.produceId,
          type: p.type,
          status: p.status,
          hectares: p.hectares,
          lines: p.lines
            ? p.lines.map((line: any) => ({
                id: line.id,
                programId: line.programId,
                productId: line.productId,
                when: line.when,
                quantity: line.quantity,
                PHI: line.PHI,
                applicationRate: line.applicationRate,
                applicationUnit: line.applicationUnit,
                applicationType: line.applicationType,
                applicationTypePerHectare: line.applicationTypePerHectare,
                comment: line.comment,
                price: line.price,
                product: line.product,
              }))
            : undefined,
          produce: p.produce,
          farmName: p.farmer?.farm?.farmName ?? `Farm #${p.farmerId ?? ''}`,
          userId: p.farmer?.userId ?? null,
        }));

        programs.sort((a, b) => {
          const aDate = a.createdDate ? Number(a.createdDate) : 0;
          const bDate = b.createdDate ? Number(b.createdDate) : 0;
          return bDate - aDate;
        });
        this.setState({programs, loading: false});
      })
      .catch((error) => {
        console.error('Failed to load programs:', error);
        this.setState({loading: false});
      });
  };

  /* ---------- table utils ---------- */

  toggleSort = (field: string) => {
    const {sortField, sortAsc} = this.state;
    this.setState({
      sortField: field,
      sortAsc: sortField === field ? !sortAsc : false,
    });
  };

  handleChangePage = (
    _e: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => this.setState({page: newPage});

  handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) =>
    this.setState({rowsPerPage: parseInt(e.target.value, 10), page: 0});

  getFilteredAndSortedData(): ProgramRow[] {
    const {programs, search, sortField, sortAsc, filterUserId} = this.state;
    const searchText = search.toLowerCase();

    const filtered = programs.filter((p) => {
      // Filter by userId if provided
      if (filterUserId && p.userId !== filterUserId) {
        return false;
      }

      return (
        (p.farmName ?? '').toLowerCase().includes(searchText) ||
        (p.type || '').toLowerCase().includes(searchText) ||
        (p.status || '').toLowerCase().includes(searchText)
      );
    });

    filtered.sort((a, b) => {
      const aVal =
        sortField === 'createdDate'
          ? a.createdDate
            ? Number(a.createdDate)
            : 0
          : (a as any)[sortField];
      const bVal =
        sortField === 'createdDate'
          ? b.createdDate
            ? Number(b.createdDate)
            : 0
          : (b as any)[sortField];

      if (aVal < bVal) return sortAsc ? -1 : 1;
      if (aVal > bVal) return sortAsc ? 1 : -1;
      return 0;
    });

    return filtered;
  }

  /* ---------- render ---------- */

  render() {
    const {search, page, rowsPerPage, sortField, sortAsc, loading, user} =
      this.state;
    const data = this.getFilteredAndSortedData();
    const pagedData = data.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage,
    );

    const userFullName = user ? user?.name + ' ' + user?.surname : '';
    const skeletonRows = Array.from({length: rowsPerPage});

    return (
      <Box className="programs-container table-screen">
        <Box>
          <Typography variant="h4" className="page-title">
            Programs{userFullName ? ': ' + userFullName : ''}
          </Typography>
          <Typography variant="body1" className="page-subtitle">
            Tailored seed, chemical and fertiliser plans for farmers
          </Typography>

          <Box className="search-row">
            <Paper component="form" className="search-paper">
              <SearchIcon className="search-icon" />
              <InputBase
                placeholder="Search farmers, programs or types..."
                value={search}
                onChange={this.handleSearchChange}
                className="search-input"
                inputProps={{'aria-label': 'search programs'}}
              />
            </Paper>

            <Box className="actions-right">
              <Button variant="outlined" className="filter-button">
                Filter
              </Button>

              <Button
                onClick={this.openCreateProgram}
                className="create-program-button"
                startIcon={<FontAwesomeIcon icon={faPlus} />}
              >
                Create New Program
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} className="table-container">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => this.toggleSort('createdDate')}
                    className="clickable"
                  >
                    Date
                    {sortField === 'createdDate' &&
                      (sortAsc ? (
                        <ArrowUpward fontSize="inherit" />
                      ) : (
                        <ArrowDownward fontSize="inherit" />
                      ))}
                  </TableCell>
                  <TableCell>Farm</TableCell>
                  <TableCell>Crop</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell># Products</TableCell>
                  <TableCell>Hectares</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading &&
                  skeletonRows.map((_, index) => (
                    <TableRow key={`program-skeleton-${index}`}>
                      <TableCell>
                        <Skeleton animation="wave" width="72%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" width="78%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" width="65%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" width="58%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" width="38%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" width="45%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" width="70%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton animation="wave" width="62%" />
                      </TableCell>
                      <TableCell>
                        <Skeleton
                          className="programs-skeleton-row-action"
                          variant="rect"
                          animation="wave"
                        />
                      </TableCell>
                    </TableRow>
                  ))}

                {!loading &&
                  pagedData.map((prog) => {
                    const dateStr = prog.createdDate
                      ? (prog.createdDate || '').substring(0, 10)
                      : '';
                    const dateISO = dateStr
                      ? new Date(parseFloat(dateStr) * 1000).toISOString()
                      : new Date().toISOString();

                    const cropName = prog.produce?.name ?? '';
                    const productsCount = prog.lines?.length ?? 0;
                    const value = `R ${
                      prog.lines
                        ? prog.lines
                            .reduce(
                              (acc, line) =>
                                acc +
                                (line.quantity ?? 0) *
                                  (line.product?.price ?? line.price ?? 0),
                              0,
                            )
                            .toFixed(2)
                        : 0
                    }`;

                    const hasLines = productsCount > 0;
                    const actionLabel = hasLines ? 'View' : 'Add';
                    const actionIcon = hasLines ? faPenToSquare : faPlus;

                    return (
                      <TableRow
                        key={prog.id}
                        hover
                        className="clickable-row"
                        onClick={() => this.handleOpenProgram(prog)}
                      >
                        <TableCell>
                          {formatDate(dateISO, 'YYYY-MM-DD')}
                        </TableCell>
                        <TableCell>{prog.farmName}</TableCell>
                        <TableCell>{cropName}</TableCell>
                        <TableCell>{prog.type || ''}</TableCell>
                        <TableCell>{productsCount}</TableCell>
                        <TableCell>{prog.hectares ?? 0}</TableCell>
                        <TableCell>{_NumberWithSpaces(value)}</TableCell>
                        <TableCell>{prog.status || ''}</TableCell>
                        <TableCell>
                          <Button
                            className="table-action-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log(prog);
                              this.handleOpenProgram(prog);
                            }}
                          >
                            <FontAwesomeIcon icon={actionIcon} />
                            {actionLabel}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                {!loading && pagedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="no-programs-cell">
                      No programs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {!loading && (
            <TablePagination
              component="div"
              count={data.length}
              page={page}
              onChangePage={this.handleChangePage}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={this.handleChangeRowsPerPage}
            />
          )}
        </Box>
      </Box>
    );
  }
}

const mapStateToProps = (state: any) => ({
  user: state.user,
  editState: state.sidepanel?.editState,
});

export default connect(mapStateToProps)(Programs);
